// Número fiscal internacional, no lugar do CNPJ.
//
// O formulário de proprietário tratava o campo como CNPJ: máscara
// 00.000.000/0000-00, exigência de exatamente 14 dígitos e um removeMask() que
// fazia `value.replace(/\D/g, '')`. Esse replace APAGA LETRAS — um código como
// o espanhol B12345678 ou o codice fiscale italiano era destruído em silêncio
// ao salvar.
//
// A maioria dos países usa só dígitos (Portugal NIF, França SIREN, Argentina
// CUIT), mas não todos: Espanha e Itália têm letras. Por isso o campo é
// alfanumérico por natureza, com regra por país onde ela é conhecida.

/** Caracteres de formatação que podem sair sem perder informação. */
const SEPARADORES = /[.\-/\s]/g;

/** Só dígitos e letras sobrevivem — diferente do antigo removeMask. */
export const stripFiscalMask = (value: string): string =>
  (value || '').replace(SEPARADORES, '').toUpperCase();

/** Código ISO de 2 letras, em caixa alta. */
const normalizarPais = (country?: string): string =>
  (country || '').trim().toUpperCase().slice(0, 2);

interface RegraFiscal {
  /** Como o valor deve ser, já sem máscara e sem o prefixo do país. */
  padrao: RegExp;
  /** Nome local do documento, para a mensagem de ajuda. */
  nome: string;
  /** Exemplo do formato esperado. */
  exemplo: string;
}

/**
 * Regras por país.
 *
 * Fonte: documento fiscal de PESSOA JURÍDICA de cada país. Onde há mais de um
 * formato aceito (ex.: França aceita SIREN de 9 e SIRET de 14), o padrão
 * cobre os dois.
 */
const REGRAS: Record<string, RegraFiscal> = {
  BR: { padrao: /^\d{14}$/,        nome: 'CNPJ',           exemplo: '00.000.000/0000-00' },
  PT: { padrao: /^\d{9}$/,         nome: 'NIF / NIPC',     exemplo: '501234567' },
  ES: { padrao: /^[A-Z]\d{7}[A-Z0-9]$/, nome: 'CIF / NIF', exemplo: 'B12345678' },
  IT: { padrao: /^(\d{11}|[A-Z0-9]{16})$/, nome: 'Partita IVA / Codice fiscale', exemplo: '12345678901' },
  FR: { padrao: /^(\d{9}|\d{14})$/, nome: 'SIREN / SIRET', exemplo: '123456789' },
  DE: { padrao: /^\d{9,13}$/,      nome: 'USt-IdNr / Steuernummer', exemplo: '123456789' },
  NL: { padrao: /^\d{9}B\d{2}$/,   nome: 'BTW-id',         exemplo: '123456789B01' },
  BE: { padrao: /^\d{10}$/,        nome: 'Ondernemingsnummer', exemplo: '0123456789' },
  GB: { padrao: /^(\d{9}|\d{12}|[A-Z0-9]{8})$/, nome: 'VAT / CRN', exemplo: '123456789' },
  AR: { padrao: /^\d{11}$/,        nome: 'CUIT',           exemplo: '20123456789' },
  US: { padrao: /^\d{9}$/,         nome: 'EIN',            exemplo: '123456789' },
};

/**
 * Tira o prefixo do país quando ele repete o código ISO.
 *
 * Números de IVA europeus costumam ser escritos com o país na frente
 * (PT501234567, DE123456789). Sem isto, validar "PT501234567" contra a regra
 * portuguesa de 9 dígitos falharia por causa do próprio prefixo.
 */
const semPrefixoDoPais = (valor: string, pais: string): string =>
  pais && valor.startsWith(pais) ? valor.slice(pais.length) : valor;

/** O país tem regra conhecida? */
export const temRegraFiscal = (country?: string): boolean =>
  Boolean(REGRAS[normalizarPais(country)]);

/** Descrição do formato esperado, para o texto de ajuda do campo. */
export const descreverFormatoFiscal = (country?: string): string | null => {
  const regra = REGRAS[normalizarPais(country)];
  return regra ? `${regra.nome} — ${regra.exemplo}` : null;
};

/**
 * Formata para exibição. A máscara pontuada só existe para o CNPJ; nos demais
 * países o valor aparece como foi digitado, em caixa alta.
 */
export const formatFiscalCode = (value: string, country?: string): string => {
  const limpo = stripFiscalMask(value);
  const pais = normalizarPais(country);

  if ((pais === 'BR' || pais === '') && /^\d{1,14}$/.test(limpo)) {
    const d = limpo;
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) {
      return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    }
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
  }

  return limpo;
};

export interface FiscalCodeCheck {
  valid: boolean;
  /** Chave de tradução do erro, quando inválido. */
  errorKey?: string;
  /** Formato esperado, para compor a mensagem. */
  esperado?: string;
}

/**
 * Valida o número fiscal.
 *
 * País com regra conhecida é validado por ela. País desconhecido cai num piso
 * permissivo (5 a 20 alfanuméricos) — recusar o que não se conhece impediria
 * cadastrar clientes de países fora da tabela.
 */
export const validateFiscalCode = (
  value: string,
  country?: string,
): FiscalCodeCheck => {
  const limpo = stripFiscalMask(value);
  if (!limpo) return { valid: false, errorKey: 'tenants.fiscalCodeRequired' };

  const pais = normalizarPais(country);
  const regra = REGRAS[pais];

  if (regra) {
    const semPrefixo = semPrefixoDoPais(limpo, pais);
    return regra.padrao.test(semPrefixo)
      ? { valid: true }
      : {
          valid: false,
          errorKey: 'tenants.fiscalCodeLength',
          esperado: `${regra.nome} — ${regra.exemplo}`,
        };
  }

  return /^[A-Z0-9]{5,20}$/.test(limpo)
    ? { valid: true }
    : { valid: false, errorKey: 'tenants.fiscalCodeLength' };
};

interface RegraPostal {
  /** Grupos de dígitos separados por hífen. [4,3] = 0000-000. */
  grupos?: number[];
  /** Tamanho máximo do texto JÁ com máscara. */
  maxLength: number;
  /** Exemplo mostrado no campo. */
  exemplo: string;
  /** Formatação própria, para quem não é só dígitos agrupados (ex.: 0000 AA). */
  formatar?: (bruto: string) => string;
  /** Validação própria, quando há formatação própria. */
  valido?: (valor: string) => boolean;
}

/**
 * Países Baixos: quatro dígitos, espaço, duas letras (1234 AB).
 *
 * O espaço FAZ PARTE do código, não é enfeite — por isso há regra própria em
 * vez de deixar o valor passar cru.
 */
const formatarNL = (bruto: string): string => {
  const limpo = (bruto || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
  const digitos = (limpo.match(/^\d{0,4}/) ?? [''])[0];
  const letras = limpo.slice(digitos.length).replace(/[^A-Z]/g, '').slice(0, 2);
  return letras ? `${digitos} ${letras}` : digitos;
};

/**
 * Código postal por país.
 *
 * Sem `grupos`, o país não tem máscara de dígitos (usa letras, como Reino
 * Unido e Países Baixos) e o valor fica como foi digitado, em caixa alta.
 */
const REGRAS_POSTAIS: Record<string, RegraPostal> = {
  BR: { grupos: [5, 3], maxLength: 9,  exemplo: '00000-000' },
  PT: { grupos: [4, 3], maxLength: 8,  exemplo: '0000-000' },
  ES: { grupos: [5],    maxLength: 5,  exemplo: '00000' },
  IT: { grupos: [5],    maxLength: 5,  exemplo: '00000' },
  FR: { grupos: [5],    maxLength: 5,  exemplo: '00000' },
  DE: { grupos: [5],    maxLength: 5,  exemplo: '00000' },
  BE: { grupos: [4],    maxLength: 4,  exemplo: '0000' },
  US: { grupos: [5, 4], maxLength: 10, exemplo: '00000-0000' },
  NL: {
    maxLength: 7,
    exemplo: '0000 AA',
    formatar: formatarNL,
    valido: (v) => /^\d{4}\s?[A-Z]{2}$/.test((v || '').toUpperCase().trim()),
  },
  GB: {                 maxLength: 8,  exemplo: 'SW1A 1AA' },
  AR: {                 maxLength: 8,  exemplo: 'C1425DKE' },
};

/** Sem país informado, vale o Brasil — é o caso da maioria dos cadastros. */
const regraPostal = (country?: string): RegraPostal =>
  REGRAS_POSTAIS[normalizarPais(country)] ?? REGRAS_POSTAIS.BR;

/** Exemplo do formato, para placeholder e texto de ajuda. */
export const exemploCodigoPostal = (country?: string): string =>
  regraPostal(country).exemplo;

/** Limite do campo. Fixo em 9 (o do Brasil) truncava formatos maiores. */
export const maxLengthCodigoPostal = (country?: string): number =>
  regraPostal(country).maxLength;

/**
 * Aplica a máscara do PAÍS. Antes, 5+3 era imposto ao Brasil e os demais
 * países ficavam sem máscara nenhuma — Portugal (0000-000) saía como texto
 * corrido, e o limite de 9 caracteres do campo era o do CEP brasileiro.
 */
export const formatZipcode = (value: string, country?: string): string => {
  const regra = regraPostal(country);
  const bruto = value || '';

  if (regra.formatar) return regra.formatar(bruto).slice(0, regra.maxLength);

  if (!regra.grupos) {
    return bruto.toUpperCase().slice(0, regra.maxLength);
  }

  const total = regra.grupos.reduce((a, b) => a + b, 0);
  const d = bruto.replace(/\D/g, '').slice(0, total);

  const partes: string[] = [];
  let i = 0;
  for (const tamanho of regra.grupos) {
    if (i >= d.length) break;
    partes.push(d.slice(i, i + tamanho));
    i += tamanho;
  }
  return partes.join('-');
};

/**
 * O código postal está completo para aquele país?
 *
 * Exigir 8 dígitos (o CEP brasileiro) impedia o cadastro de qualquer outro
 * país. Países sem máscara de dígitos só exigem um mínimo razoável.
 */
export const codigoPostalValido = (value: string, country?: string): boolean => {
  const regra = regraPostal(country);
  const bruto = (value || '').trim();
  if (regra.valido) return regra.valido(bruto);
  if (!regra.grupos) return bruto.length >= 3;
  const total = regra.grupos.reduce((a, b) => a + b, 0);
  return bruto.replace(/\D/g, '').length === total;
};

/**
 * Valor a ENVIAR ao servidor.
 *
 * Países de dígitos guardam só os dígitos — a máscara é reconstruída na
 * leitura, então a pontuação é descartável. Nos demais o separador FAZ PARTE
 * do código (1234 AB, SW1A 1AA) e some para sempre se for apagado, porque não
 * há máscara que o recoloque.
 *
 * Antes isto era um `.replace(/[\s-]/g, '')` fixo nos formulários, que
 * destruía o espaço desses países.
 */
export const normalizarCodigoPostal = (value: string, country?: string): string => {
  const regra = regraPostal(country);
  const bruto = (value || '').trim();
  return regra.grupos ? bruto.replace(/\D/g, '') : bruto.toUpperCase();
};

export const PAISES_COM_REGRA = Object.keys(REGRAS);
